import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Menu } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteMenu } from '../hooks/use-menu-mutations';

export interface DeleteMenuDialogProps {
  storeId: string;
  menu: Menu | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

export function DeleteMenuDialog({
  storeId,
  menu,
  onOpenChange,
  onDeleted,
}: DeleteMenuDialogProps) {
  const { t } = useTranslation(['menus', 'common']);
  const remove = useDeleteMenu(storeId);
  const message = useErrorMessage();

  return (
    <AlertDialog
      open={menu !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('menus:delete.title', { title: menu?.title ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('menus:delete.description', { handle: menu?.handle ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{message(remove.error)}</AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (menu) {
                remove.mutate(menu.id, {
                  onSuccess: () => {
                    onOpenChange(false);
                    onDeleted?.();
                  },
                });
              }
            }}
          >
            {remove.isPending ? t('menus:delete.submitting') : t('menus:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
